import apiService from "../services/apiService";

export type AssetShipmentDocument = {
  id: string;
  individual_asset_id: string;
  docs_type: string;
  name: string;
  s3_file_key?: string;
  url: string;
};

const toText = (value: unknown) => (value == null ? "" : String(value).trim());

export const normalizeAssetShipmentDocument = (
  raw: Record<string, unknown>,
  fallbackIndividualAssetId?: string,
): AssetShipmentDocument | null => {
  const id = toText(raw.id);
  const url = toText(raw.url ?? raw.s3_file_key);
  const name = toText(raw.name ?? raw.file_name ?? raw.filename) || "Document";
  const docs_type = toText(raw.docs_type ?? raw.docType ?? raw.doc_type);
  const individual_asset_id = toText(
    raw.individual_asset_id ?? raw.individualAssetId ?? fallbackIndividualAssetId,
  );

  if (!id || !url || !docs_type) return null;

  return {
    id,
    individual_asset_id,
    docs_type,
    name,
    s3_file_key: raw.s3_file_key != null ? toText(raw.s3_file_key) : undefined,
    url,
  };
};

export const extractAssetShipmentDocumentsFromResponse = (
  response: unknown,
  fallbackIndividualAssetId?: string,
): AssetShipmentDocument[] => {
  const root = response as Record<string, unknown> | null | undefined;
  const payload = root?.data ?? response;
  if (!Array.isArray(payload)) return [];
  return payload
    .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
    .map((row) => normalizeAssetShipmentDocument(row, fallbackIndividualAssetId))
    .filter((doc): doc is AssetShipmentDocument => doc != null);
};

const normalizeDocType = (value: string) =>
  value.toLowerCase().replace(/[\s_-]+/g, "");

export const isTaxInvoiceDocument = (doc: AssetShipmentDocument) => {
  const type = normalizeDocType(doc.docs_type);
  return type === "taxinvoice" || type === "taxinvoices" || type === "invoice";
};

export const isEwayBillDocument = (doc: AssetShipmentDocument) => {
  const type = normalizeDocType(doc.docs_type);
  return type === "eway" || type === "ewaybill" || type === "ewaybills" || type === "ewb";
};

export const filterTaxInvoiceDocuments = (docs: AssetShipmentDocument[]) =>
  docs.filter(isTaxInvoiceDocument);

export const filterEwayBillDocuments = (docs: AssetShipmentDocument[]) =>
  docs.filter(isEwayBillDocument);

export const downloadAssetShipmentDocument = async (doc: AssetShipmentDocument) => {
  const fileRef = doc.url || doc.s3_file_key;
  if (!fileRef) return;

  const triggerBlobDownload = (blob: Blob) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = doc.name || "document";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  };

  try {
    const blob = await apiService.post<Blob>(
      "/data-room/get-file",
      { url: fileRef },
      { responseType: "blob" },
    );

    if (blob.type === "application/json") {
      const errBody = JSON.parse(await blob.text());
      throw new Error(errBody?.message || "Failed to download file");
    }
    if (!blob.size || blob.type.includes("text/html")) {
      throw new Error("Invalid file response");
    }

    triggerBlobDownload(blob);
  } catch (error) {
    console.error("Failed to download shipment document:", error);
    window.open(fileRef, "_blank", "noopener,noreferrer");
  }
};
